import { TableType } from '@/coreDomainsModule/table';
import { BASE_TABLE_CONTAINER_ID, BASE_TABLE_CONTENT_ID, STICKY_COLUMN_WIDTH, TABLE_HEADER_ID } from '../constants';

/**
 * 列幅変更関連の処理をまとめる
 */
export class ResizeStateService {
  /**
   * テーブルのコンテナのIDを取得
   * @param tableType - テーブルの種類
   * @returns - テーブルのコンテナのID
   */
  public static getTableContainerId = (tableType: TableType): string => {
    return BASE_TABLE_CONTAINER_ID + `-${tableType}`;
  };

  /**
   * 描画されている実際のテーブルコンテナの幅を取得
   * @param tableType - テーブルの種類
   * @returns - テーブルコンテナの幅
   */
  public static getCurrentTableContainerWidth = (tableType: TableType): number | undefined => {
    const tableContainer = document.getElementById(this.getTableContainerId(tableType));
    return tableContainer?.clientWidth;
  };

  /**
   * テーブルのコンテンツのIDを取得
   * @param tableType - テーブルの種類
   * @returns - テーブルのコンテンツのID
   */
  public static getTableContentId = (tableType: TableType): string => {
    return BASE_TABLE_CONTENT_ID + `-${tableType}`;
  };

  /**
   * 描画されている実際のテーブルの幅を取得
   * @param tableType - テーブルの種類
   * @returns - テーブルの幅
   */
  public static getCurrentTableContentWidth = (tableType: TableType): number | undefined => {
    const tableContent = document.getElementById(this.getTableContentId(tableType));
    return tableContent?.clientWidth;
  };

  /**
   * テーブルのヘッダーのIDを列ごとに取得
   * @param index - 列のインデックス
   * @returns - テーブルのヘッダーのID
   */
  public static getTableHeaderId = (index: number): string => {
    return TABLE_HEADER_ID + `-${index}`;
  };

  /**
   * 描画されている実際のテーブルの列幅を取得
   * @param index - 列のインデックス
   * @returns - テーブルの列幅
   */
  public static getCurrentColumnWidth = (index: number): number | undefined => {
    const header = document.getElementById(this.getTableHeaderId(index));
    return header?.clientWidth;
  };

  /**
   * 描画されている実際のテーブル幅から全固定列の幅を引いた、可変列を設置可能な余白を取得
   * @param tableType - テーブルの種類
   * @param stableColumnCount - 固定列の数
   * @returns - 可変列を設置可能な余白
   */
  public static getResizableColumnsSpaceWidth = (tableType: TableType, stableColumnCount: number): number | undefined => {
    const baseTableWidth = this.getCurrentTableContainerWidth(tableType);
    const fixedColumnWidth = STICKY_COLUMN_WIDTH * stableColumnCount;
    return baseTableWidth ? baseTableWidth - fixedColumnWidth : undefined;
  };

  /**
   * 全体の幅に対する列幅の割合値を取得
   * @param tableType - テーブルの種類
   * @param stableColumnCount - 固定列の数
   * @param width - 列幅
   * @returns - 列幅の割合値
   */
  public static getResizableColumnWidthRatio = (tableType: TableType, stableColumnCount: number, width: number): number | undefined => {
    const allResizableColumnsWidth = this.getResizableColumnsSpaceWidth(tableType, stableColumnCount);
    return allResizableColumnsWidth ? width / allResizableColumnsWidth : undefined;
  };

  /**
   * 列幅の割合値から列幅を計算して取得
   * @param tableType - テーブルの種類
   * @param stableColumnCount - 固定列の数
   * @param ratio - 列幅の割合値
   * @returns - 列幅
   */
  public static getResizableColumnWidth = (tableType: TableType, stableColumnCount: number, ratio: number): number | undefined => {
    const allResizableColumnsWidth = this.getResizableColumnsSpaceWidth(tableType, stableColumnCount);
    return allResizableColumnsWidth ? allResizableColumnsWidth * ratio : undefined;
  };
}
